import { SxProps, Theme } from '@mui/material';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useRef, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { SelectTrigger, SelectTriggerVariant } from 'src/components/SelectTrigger';
import { DISPLAY_DATE_FORMAT, DISPLAY_DATE_TIME_FORMAT } from 'src/constants/date';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

export type DateTriggerPrecision = 'date' | 'date-time';

const DEFAULT_FORMAT: Record<DateTriggerPrecision, string> = {
  date: DISPLAY_DATE_FORMAT,
  'date-time': DISPLAY_DATE_TIME_FORMAT,
};

/** The word every empty property row in a side panel says, date or not. */
const DEFAULT_PLACEHOLDER = 'Empty';

/** The trigger is the picker's anchor and its visible value, so the field slot renders nothing. */
const HiddenPickerField = () => null;

export interface DateTriggerProps {
  /** Accessible name; the row's own label. */
  label: string;
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  precision?: DateTriggerPrecision;
  /** Day.js format for the rendered value; defaults to the shape `precision` implies. */
  format?: string;
  /** Shown while the row is empty. */
  placeholder?: string;
  /** IANA zone the value is shown and picked in; the browser's zone when omitted. */
  timezone?: string;
  disabled?: boolean;
  /** Replaces the trailing slot with a spinner while a save is in flight. */
  loading?: boolean;
  onClear?: () => void;
  variant?: SelectTriggerVariant;
  sx?: SxProps<Theme>;
}

/**
 * The date counterpart of `SelectTrigger`: one control for every side-panel row
 * that opens a calendar, so a date row lines up with a select row instead of
 * carrying a text field's geometry.
 */
export const DateTrigger = ({
  label,
  value,
  onChange,
  precision = 'date',
  format = DEFAULT_FORMAT[precision],
  placeholder = DEFAULT_PLACEHOLDER,
  timezone,
  disabled,
  loading,
  onClear,
  variant = 'inline',
  sx,
}: DateTriggerProps) => {
  const [open, setOpen] = useState(false);
  // A date-time picker walks the user through day, hour, minute and meridiem,
  // emitting a change per step. Only the accepted value reaches the caller, so a
  // row that saves on change writes once instead of storing every half-picked date.
  const [draft, setDraft] = useState<Dayjs | null>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);
  // The label and the calendar must read the same zone, or a row can name a
  // different day than the one the picker highlights under it.
  const validValue = value?.isValid() ? value : null;
  const shownValue = validValue && timezone ? validValue.tz(timezone) : validValue;
  const formattedValue = shownValue ? shownValue.format(format) : null;

  const close = () => {
    setOpen(false);
    setDraft(null);
  };

  const pickerProps = {
    open,
    value: draft ?? value,
    timezone,
    disabled,
    onChange: (next: Dayjs | null) => setDraft(next),
    onAccept: (accepted: Dayjs | null) => {
      setDraft(null);
      onChange(accepted);
    },
    onClose: close,
    desktopModeMediaQuery: '@media(min-width:0px)',
    slots: { field: HiddenPickerField },
    slotProps: { popper: { anchorEl: anchorRef.current } },
  };

  return (
    <>
      <SelectTrigger
        ref={anchorRef}
        variant={variant}
        open={open}
        disabled={disabled}
        loading={loading}
        onClear={formattedValue ? onClear : undefined}
        onClick={() => setOpen(true)}
        icon={<DynamicIcon name="calendar" library="lucide" />}
        label={formattedValue ?? placeholder}
        aria-label={formattedValue ? `${label}: ${formattedValue}` : label}
        sx={sx}
      />
      {precision === 'date-time' ? (
        <DateTimePicker {...pickerProps} />
      ) : (
        <DatePicker {...pickerProps} />
      )}
    </>
  );
};
